
import { GoogleBook, GoogleBooksVolumeInfo } from "./types";
import { plainTextFromHtml } from "@/utils/textCleaners";

export const transformGoogleBookData = (item: GoogleBooksVolumeInfo): GoogleBook | null => {
  if (!item?.volumeInfo?.title) return null;
  
  const { volumeInfo } = item;
  
  // Get the best available cover image with maximum quality
  const getCoverUrl = () => {
    const images = volumeInfo.imageLinks;
    if (!images) {
      // Google has no cover for this volume. Return undefined (NOT a fabricated
      // content-endpoint URL): a built URL makes Google serve its generic
      // "image not available" GIF with HTTP 200, which fires the <img> onLoad
      // and silently masks the miss. Returning undefined lets EnhancedBookCover's
      // recovery chain (Supabase cache → Open Library → Archive.org → placeholder)
      // run instead.
      return undefined;
    }
    
    // Prefer highest quality images and enhance them
    const baseUrl = images.extraLarge || 
                   images.large || 
                   images.medium || 
                   images.small || 
                   images.thumbnail || 
                   images.smallThumbnail;
    
    if (!baseUrl) return undefined;
    
    // Normalize URL for reliability across browsers
    let normalizedUrl = baseUrl.replace(/^http:\/\//, 'https://');

    // If using Google Books content endpoint, ensure expected params are present
    if (/books\.google(?:usercontent)?\.com\/books\/content/.test(normalizedUrl)) {
      const hasImg = /[?&]img=/.test(normalizedUrl);
      const hasPrintsec = /[?&]printsec=/.test(normalizedUrl);

      if (!hasImg) {
        const sep = normalizedUrl.includes('?') ? '&' : '?';
        normalizedUrl = `${normalizedUrl}${sep}img=1`;
      }
      if (!hasPrintsec) {
        const sep = normalizedUrl.includes('?') ? '&' : '?';
        normalizedUrl = `${normalizedUrl}${sep}printsec=frontcover`;
      }
      // Do not force zoom; keep server defaults for best compatibility
    }

    return normalizedUrl;
  };
  
  // Extract ISBN (prefer ISBN_13, fallback to ISBN_10)
  const getIsbn = () => {
    const identifiers = volumeInfo.industryIdentifiers || [];
    const isbn13 = identifiers.find(id => id.type === 'ISBN_13');
    const isbn10 = identifiers.find(id => id.type === 'ISBN_10');
    return isbn13?.identifier || isbn10?.identifier;
  };
  
  const result = {
    id: item.id,
    title: volumeInfo.title,
    subtitle: volumeInfo.subtitle,
    author: volumeInfo.authors?.[0] || 'Unknown Author',
    // Google returns HTML here — <p> and <b> tags and entities — and it
    // used to pass through raw. Same cleaner as the Apple path.
    description: plainTextFromHtml(volumeInfo.description),
    coverUrl: getCoverUrl(),
    publishedDate: volumeInfo.publishedDate,
    pageCount: volumeInfo.pageCount,
    categories: volumeInfo.categories,
    averageRating: volumeInfo.averageRating,
    ratingsCount: volumeInfo.ratingsCount,
    previewLink: volumeInfo.previewLink,
    infoLink: volumeInfo.infoLink,
    isbn: getIsbn()
  };
  
  return result;
};
